<template>
  <div class="container page">
    <van-nav-bar :title="$t('withdraw.with_center')" class="nav-bar">
      <template #left>
        <van-icon name="arrow-left" color="#fff" @click="back()"/>
      </template>
      <template #right>
        <van-icon name="setting-o" color="#fff" size="22" @click="openBindDrawer"/>
      </template>
    </van-nav-bar>

    <!-- 绑定地址抽屉 -->
    <van-popup v-model="showBindDrawer" position="bottom" round :style="{ height: '75%' }">
      <div class="drawer">
        <!-- 标题栏 -->
        <div class="drawer-header">
          <span class="drawer-title">{{ $t('withdraw.withdraw_address_info') }}</span>
          <van-icon name="cross" @click="showBindDrawer = false"/>
        </div>

        <!-- Tab 切换 -->
        <div class="drawer-tabs">
          <div
            class="drawer-tab"
            :class="{ active: bindTab === 'bank' }"
            @click="bindTab = 'bank'"
          >{{ $t('withdraw.bank_cards') }}</div>
          <div
            class="drawer-tab"
            :class="{ active: bindTab === 'crypto' }"
            @click="bindTab = 'crypto'"
          >{{ $t('withdraw.crypto_addresses') }}</div>
        </div>

        <!-- 银行卡表单 -->
        <div v-if="bindTab === 'bank'" class="drawer-form">
          <div class="form-field">
            <div class="field-label">{{ $t('withdraw.bank_name') }}</div>
            <div class="field-input" @click="showBankPicker = true">
              <span :class="bind.bank_name ? 'field-value' : 'field-placeholder'">
                {{ bind.bank_name || $t('withdraw.select_bank') }}
              </span>
              <van-icon name="arrow" color="#ccc"/>
            </div>
          </div>
          <div class="form-field">
            <div class="field-label">{{ $t('withdraw.account_holder') }}</div>
            <input class="field-input-raw" v-model="bind.account_holder" :placeholder="$t('withdraw.account_holder_tip')"/>
          </div>
          <div class="form-field">
            <div class="field-label">{{ $t('withdraw.account_number') }}</div>
            <input class="field-input-raw" v-model="bind.account_number" type="number" :placeholder="$t('withdraw.account_number_tip')"/>
          </div>
          <van-button class="drawer-btn" block @click="submitBank">{{ $t('common.confirm') }}</van-button>
        </div>

        <!-- 加密货币表单 -->
        <div v-if="bindTab === 'crypto'" class="drawer-form">
          <div class="form-field">
            <div class="field-label">{{ $t('withdraw.coin_type') }}</div>
            <div class="field-input" @click="showCoinPicker = true">
              <span :class="bind.crypto_type ? 'field-value' : 'field-placeholder'">
                {{ bind.crypto_type || $t('withdraw.coin_type') }}
              </span>
              <van-icon name="arrow" color="#ccc"/>
            </div>
          </div>
          <div class="form-field">
            <div class="field-label">{{ $t('withdraw.network_type') }}</div>
            <div class="field-input" @click="openNetworkPicker">
              <span :class="bind.network ? 'field-value' : 'field-placeholder'">
                {{ bind.network || $t('withdraw.select_network') }}
              </span>
              <van-icon name="arrow" color="#ccc"/>
            </div>
          </div>
          <div class="form-field">
            <div class="field-label">{{ $t('withdraw.wallet_address') }}</div>
            <input class="field-input-raw" v-model="bind.address" :placeholder="$t('withdraw.wallet_address_tip')"/>
          </div>
          <van-button class="drawer-btn" block @click="submitCrypto">{{ $t('common.confirm') }}</van-button>
        </div>
      </div>

      <!-- 银行选择器 -->
      <van-popup v-model="showBankPicker" round position="bottom" :style="{ height: '60%' }">
        <van-picker show-toolbar :columns="bankList"
          :confirm-button-text="$t('common.confirm')"
          :cancel-button-text="$t('common.cancel')"
          @confirm="v => { bind.bank_name = v; showBankPicker = false }"
          @cancel="showBankPicker = false"
        />
      </van-popup>

      <!-- 币种选择器 -->
      <van-popup v-model="showCoinPicker" round position="bottom" :style="{ height: '35%' }">
        <van-picker show-toolbar :columns="coinTypes"
          :confirm-button-text="$t('common.confirm')"
          :cancel-button-text="$t('common.cancel')"
          @confirm="onCoinConfirm"
          @cancel="showCoinPicker = false"
        />
      </van-popup>

      <!-- 网络选择器 -->
      <van-popup v-model="showNetworkPicker" round position="bottom" :style="{ height: '35%' }">
        <van-picker show-toolbar :columns="networkOptions"
          :confirm-button-text="$t('common.confirm')"
          :cancel-button-text="$t('common.cancel')"
          @confirm="v => { bind.network = v.text || v; showNetworkPicker = false }"
          @cancel="showNetworkPicker = false"
        />
      </van-popup>
    </van-popup>
    <div class="main">
      <!-- 提现地址信息 -->
      <div v-if="addressInfo" class="withdrawAddress">
        <div class="address-header">
          <span>{{ isBankCard ? $t('withdraw.bank_card_info') : $t('withdraw.withdraw_address_info') }}</span>
          <span v-if="hasBoth" class="switch-btn" @click="toggleAddressType">{{ isBankCard ? $t('withdraw.crypto_addresses') : $t('withdraw.bank_cards') }} ›</span>
        </div>
        <div class="address-card">
          <!-- 银行卡模式 -->
          <template v-if="isBankCard">
            <div class="address-row">
              <span class="label">{{$t("withdraw.bank_name")}}:</span>
              <span class="value">{{addressInfo.bankinfo}}</span>
            </div>
            <div class="address-row">
              <span class="label">{{$t("withdraw.account_holder")}}:</span>
              <span class="value">{{addressInfo.username}}</span>
            </div>
            <div class="address-row">
              <span class="label">{{$t("withdraw.account_number")}}:</span>
              <span class="value address-text">{{addressInfo.bankid}}</span>
            </div>
          </template>
          <!-- 加密货币模式 -->
          <template v-else>
            <div class="address-row">
              <span class="label">{{$t("withdraw.coin_type")}}:</span>
              <span class="value">{{addressInfo.bankinfo}}</span>
            </div>
            <div class="address-row">
              <span class="label">{{$t("withdraw.network_type")}}:</span>
              <span class="value">{{addressInfo.bankfenhang}}</span>
            </div>
            <div class="address-row">
              <span class="label">{{$t("withdraw.wallet_address")}}:</span>
              <span class="value address-text">{{addressInfo.bankid}}</span>
            </div>
          </template>
        </div>
      </div>

      <div class="withdrawMoney">
        <span>{{$t("withdraw.with_money")}} ({{$t("reservation.unit")}})</span>
        <div class="money">
          <div class="moneyNumber">
            <van-field v-model="withdraw_money" type="number" />
          </div>
          <span class="all" @click="allMoeny()">{{$t("index.all")}}</span>
        </div>

        <!-- 提款密码输入框 -->
        <div class="password-field">
          <span>{{$t("setting.finance_pwd")}}</span>
          <div class="password-input">
            <van-field
              v-model="pay_password"
              type="password"
              :placeholder="$t('auth.pay_pwd_place')"
              maxlength="6"
            />
          </div>
        </div>

        <div class="information">
          <div class="description">
            <van-popover v-model="showPopover" trigger="click">
              <div class="popover-body" style="padding: 10px;">
                <p>1.{{$t("withdraw.single_limit")}}{{$t("withdraw.low")}}{{this.withdrawRole.min}}{{$t("withdraw.heigh")}}{{this.withdrawRole.max}}{{$t("reservation.unit")}}</p>
                <p>2.{{$t("withdraw.with_num")}}{{this.withdrawRole.num}}{{$t("withdraw.number")}}</p>
                <p>3.{{$t("withdraw.with_tip")}}</p></div>
              <template #reference @click="withdrawInfo()">
                <van-icon name="info-o" />
                {{$t("withdraw.limit_desc")}}
              </template>
            </van-popover>
          </div>

          <div class="balance">
            <span>{{$t("my.balance")}}：</span>
            <span class="number">{{ this.userInfo.money }}{{$t("reservation.ge")}}</span>
          </div>
        </div>
      </div>
      <van-button class="withdraw_btn" type="default" :loading="submitting" :disabled="submitting" @click="doWithdraw()"> {{$t("withdraw.immediately_withdraw")}}</van-button>

    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      showPopover: false,
      showBindDrawer: false,
      showBankPicker: false,
      showCoinPicker: false,
      showNetworkPicker: false,
      bindTab: 'bank',
      bind: {
        bank_name: '', account_holder: '', account_number: '',
        crypto_type: '', network: '', address: ''
      },
      bankList: [
        'KB국민은행','신한은행','우리은행','KEB하나은행','NH농협은행',
        'IBK기업은행','SC제일은행','씨티은행','카카오뱅크','토스뱅크',
        '케이뱅크','부산은행','경남은행','대구은행','광주은행','전북은행',
        '제주은행','수협은행','우체국'
      ],
      coinTypes: [
        { text: 'USDC', value: 'USDC' },
        { text: 'BTC',  value: 'BTC'  },
        { text: 'ETH',  value: 'ETH'  }
      ],
      networkConfig: {
        USDC: ['ERC20','TRC20','Solana','Polygon','Arbitrum'],
        BTC:  ['Bitcoin','BTC (SegWit)','Lightning Network'],
        ETH:  ['ERC20','Arbitrum','Optimism','Polygon','zkSync']
      },
      networkOptions: [],
      withdraw_money: "",
      pay_password: "",
      userInfo: {},
      withdrawRole: {},
      defaultAddressId: null,
      addressInfo: null,
      isBankCard: true,
      bankInfo: null,
      cryptoList: [],
      hasBoth: false,
      submitting: false,
    };
  },
  methods: {
    back(){
      return window.history.back();
    },
    openBindDrawer() {
      // 回显已有数据
      if (this.bankInfo) {
        this.bind.bank_name      = this.bankInfo.bankinfo || '';
        this.bind.account_holder = this.bankInfo.username || '';
        this.bind.account_number = this.bankInfo.bankid   || '';
      }
      if (this.cryptoList.length > 0) {
        const c = this.cryptoList[0];
        this.bind.crypto_type = c.bankinfo  || '';
        this.bind.network     = c.bankfenhang || '';
        this.bind.address     = c.bankid    || '';
        this.networkOptions   = this.networkConfig[c.bankinfo] || [];
      }
      // 默认显示已有数据的 tab
      this.bindTab = this.bankInfo ? 'bank' : 'crypto';
      this.showBindDrawer = true;
    },
    onCoinConfirm(item) {
      this.bind.crypto_type = item.text;
      this.bind.network     = '';
      this.networkOptions   = this.networkConfig[item.value] || [];
      this.showCoinPicker   = false;
    },
    openNetworkPicker() {
      if (!this.bind.crypto_type) {
        this.$toast.fail(this.$t('withdraw.coin_type'));
        return;
      }
      this.showNetworkPicker = true;
    },
    submitBank() {
      if (!this.bind.bank_name)      { this.$toast.fail(this.$t('withdraw.select_bank'));        return; }
      if (!this.bind.account_holder) { this.$toast.fail(this.$t('withdraw.account_holder_tip')); return; }
      if (!this.bind.account_number) { this.$toast.fail(this.$t('withdraw.account_number_tip')); return; }
      this.$http({
        method: 'post',
        data: { bank: this.bind.bank_name, username: this.bind.account_holder, bankid: this.bind.account_number },
        url: 'user_set_bank'
      }).then(res => {
        if (res.code === 200) {
          this.$toast(res.msg);
          this.showBindDrawer = false;
          this.getCryptoAddresses('bank');
        } else { this.$toast(res.msg); }
      });
    },
    submitCrypto() {
      if (!this.bind.crypto_type) { this.$toast.fail(this.$t('withdraw.coin_type'));          return; }
      if (!this.bind.network)     { this.$toast.fail(this.$t('withdraw.select_network'));      return; }
      if (!this.bind.address)     { this.$toast.fail(this.$t('withdraw.wallet_address_tip')); return; }
      this.$http({
        method: 'post',
        data: { address: this.bind.address, crypto_type: this.bind.crypto_type, network: this.bind.network },
        url: 'user_set_crypto_address'
      }).then(res => {
        if (res.code === 200) {
          this.$toast(res.msg);
          this.showBindDrawer = false;
          this.getCryptoAddresses('crypto');
        } else { this.$toast(res.msg); }
      });
    },
    getUserInfo(){
      this.$http({
        method: 'get',
        url: 'user_info'
      }).then(res => {
        if(res.code === 200){
          this.userInfo = res.data;
          this.name = res.data.name;

          // 检查是否设置了提现密码
          if(!res.data.paypassword || res.data.paypassword === '' || res.data.paypassword === null){
            // 没有提现密码，跳转到设置提现密码页面
            this.$toast(this.$t("withdraw.please_set_pay_password_first"));
            this.$router.push({path: '/SetPayPassword'});
          } else {
            // 有提现密码，继续检查提现地址
            this.getCryptoAddresses();
          }
        } else if(res.code === 401){
          this.$toast(res.msg);
        }
      })
    },
    getUserWithdrawRole(){
      this.$http({
        method: 'get',
        url: 'user_get_withdraw_role'
      }).then(res => {
        if(res.code === 200){
          this.withdrawRole = res.data;
        } else if(res.code === 401){
          this.$toast(res.msg);
        }
      })
    },
    getCryptoAddresses(forceTo){
      return this.$http({
        method: 'get',
        url: 'user_get_bank'
      }).then(res => {
        if(res.code === 200){
          this.bankInfo = res.data.bank_info || null;
          this.cryptoList = res.data.is_crypto ? res.data.crypto_list : [];
          const hasCrypto = this.cryptoList.length > 0;
          this.hasBoth = !!this.bankInfo && hasCrypto;
          // 若指定了强制切换目标，优先使用
          if (forceTo === 'bank' && this.bankInfo) {
            this.isBankCard = true;
            this.addressInfo = this.bankInfo;
            this.defaultAddressId = this.bankInfo.id;
          } else if (forceTo === 'crypto' && hasCrypto) {
            this.isBankCard = false;
            this.addressInfo = this.cryptoList[0];
            this.defaultAddressId = this.cryptoList[0].id;
          } else if(this.bankInfo){
            this.isBankCard = true;
            this.addressInfo = this.bankInfo;
            this.defaultAddressId = this.bankInfo.id;
          } else if(hasCrypto){
            this.isBankCard = false;
            this.addressInfo = this.cryptoList[0];
            this.defaultAddressId = this.cryptoList[0].id;
          } else {
            this.$toast(this.$t("withdraw.please_bind_address_first"));
            this.$router.push({path: '/BindCard'});
          }
        } else if(res.code === 401){
          this.$toast(res.msg);
        }
      })
    },
    toggleAddressType(){
      if(this.isBankCard){
        this.isBankCard = false;
        this.addressInfo = this.cryptoList[0];
        this.defaultAddressId = this.cryptoList[0].id;
      } else {
        this.isBankCard = true;
        this.addressInfo = this.bankInfo;
        this.defaultAddressId = this.bankInfo.id;
      }
    },
    allMoeny(){
      this.withdraw_money = this.userInfo.money;
    },
    doWithdraw(){
      const money = parseFloat(this.withdraw_money);

      // 验证提现金额
      if(!money || money <= 0){
        this.$toast(this.$t("setting.correct_money"));
        return false;
      }

      // 前端余额校验
      if(money > parseFloat(this.userInfo.money)){
        this.$toast(this.$t("reservation.balance_enough"));
        return false;
      }

      // 前端最小/最大金额校验
      if(this.withdrawRole.min && money < parseFloat(this.withdrawRole.min)){
        this.$toast(this.$t("withdraw.low") + this.withdrawRole.min);
        return false;
      }
      if(this.withdrawRole.max && money > parseFloat(this.withdrawRole.max)){
        this.$toast(this.$t("withdraw.heigh") + this.withdrawRole.max);
        return false;
      }

      // 验证提款密码
      if(!this.pay_password || this.pay_password === ''){
        this.$toast(this.$t("auth.pay_pwd_place"));
        return false;
      }

      // 验证密码长度
      if(this.pay_password.length !== 6){
        this.$toast(this.$t("auth.pay_pwd_error"));
        return false;
      }

      // 验证是否有默认地址
      if(!this.defaultAddressId){
        this.$toast(this.$t("withdraw.please_bind_address_first"));
        this.$router.push({path: '/BindCard'});
        return false;
      }

      // 提交提现请求
      this.submitting = true;
      this.$http({
        method: 'post',
        data: {
          money: money,
          paypassword: this.pay_password,
          address_id: this.defaultAddressId
        },
        url: 'user_set_withdraw'
      }).then(res => {
        this.submitting = false;
        if(res.code === 200){
          this.$toast(res.msg);
          this.withdraw_money = "";
          this.pay_password = "";
          this.getUserInfo();
        } else if(res.code === 401){
          this.$toast(res.msg);
        }
      }).catch(() => {
        this.submitting = false;
      })
    },
    withdrawInfo(){
      this.showPopover = true;
    }
  },
  created() {
    if(!localStorage.getItem('token')){
      this.$router.push({path: '/Login'})
    } else {
      this.getUserInfo(); // 会在内部调用 getCryptoAddresses
      this.getUserWithdrawRole();
    }
  }
};
</script>

<style lang='less' scoped>
@import "../../assets/css/base.css";
.van-cell {
  font-size: 35px;
  line-height: 80px;
}
.container p{
  padding: 0 15px;
  margin-top: 15px;
  font-size: 30px;
  color: #dc2037;
}
.container .main{
  display: flex;
  flex-direction: column;
  background-color: #f2f2f5;
  height: calc(100% - 50px);
  position: relative;
}

/* 提现地址信息展示 */
.withdrawAddress {
  background-color: #fff;
  margin-bottom: 15px;
  padding: 20px;
  border-radius: 8px;
}

.address-header {
  margin-bottom: 20px;
  font-size: 32px;
  font-weight: 600;
  color: #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.switch-btn {
  font-size: 26px;
  color: #7e5678;
  font-weight: normal;
  cursor: pointer;
}

.address-card {
  background-color: #f7f8fa;
  border-radius: 8px;
  padding: 20px;
}

.address-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
  font-size: 28px;
}

.address-row:last-child {
  margin-bottom: 0;
}

.address-row .label {
  color: #646566;
  font-weight: 500;
  min-width: 140px;
  flex-shrink: 0;
}

.address-row .value {
  color: #323233;
  flex: 1;
  word-break: break-all;
}

.address-row .address-text {
  font-family: monospace;
  font-size: 24px;
  line-height: 1.6;
}

.container .main .withdrawMoney{
  display: flex;
  flex-direction: column;
  color: #000;
  padding: 0 20px;
  white-space: nowrap;
  font-size: 35px;
  background-color: #fff;
}
.container .main .withdrawMoney span {
  padding: 10px 0;
}
.container .main .withdrawMoney .money{
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  border-bottom: 1px solid #f2f2f5;
}
.container .main .withdrawMoney .money .moneyNumber{
  font-size: 50px;
  display: flex;
  flex-direction: row;
}
.container .main .withdrawMoney span {
  padding: 10px 0;
}
.container .main .withdrawMoney .money .all{
  color: #d10404;
}
.container .main .withdrawMoney .money .moneyNumber .van-cell {
  font-size: 50px;
  padding: 0!important;
}
.container .main .withdrawMoney .password-field {
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f2f2f5;
}
.container .main .withdrawMoney .password-field span {
  padding: 10px 0;
  font-size: 35px;
}
.container .main .withdrawMoney .password-field .password-input {
  display: flex;
  align-items: center;
}
.container .main .withdrawMoney .password-field .password-input .van-cell {
  font-size: 35px;
  padding: 0!important;
}
.container .main .withdrawMoney .information{
  padding-bottom: 30px;
}
.container .main .withdrawMoney .information .description {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 10px 0;
}
.container .main .withdrawMoney span{
  padding: 10px 0;
}
.container .main .withdrawMoney .information .balance .number{
  color: #d10404;
}
.withdraw_btn {
  margin: 20px 30px 0;
  height: 80px;
  line-height: 1.22667rem;
  border-radius: 50px;
  color: #fff;
  font-size: 30px;
  font-weight: bolder;
  border: none;
  background: linear-gradient(90deg,#e6c3a1,#7e5678);
}

/* ===== 绑定地址抽屉 ===== */
.drawer {
  padding: 30px 32px 40px;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}
.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  font-size: 34px;
}
.drawer-title {
  font-weight: 700;
  color: #222;
}

/* Tab */
.drawer-tabs {
  display: flex;
  background: #f4f4f8;
  border-radius: 14px;
  padding: 6px;
  margin-bottom: 36px;
}
.drawer-tab {
  flex: 1;
  height: 68px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 500;
  color: #999;
  border-radius: 10px;
  transition: all 0.25s ease;
}
.drawer-tab.active {
  background: #fff;
  color: #7e5678;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

/* 表单 */
.drawer-form {
  flex: 1;
  overflow-y: auto;
}
.form-field {
  margin-bottom: 32px;
}
.field-label {
  font-size: 26px;
  color: #999;
  margin-bottom: 12px;
}
.field-input {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 88px;
  border-radius: 14px;
  background: #f7f7f9;
  padding: 0 24px;
  font-size: 30px;
}
.field-value    { color: #222; }
.field-placeholder { color: #ccc; }
.field-input-raw {
  width: 100%;
  height: 88px;
  border: none;
  outline: none;
  border-radius: 14px;
  background: #f7f7f9;
  padding: 0 24px;
  font-size: 30px;
  color: #222;
  box-sizing: border-box;
  &::placeholder { color: #ccc; }
}
.drawer-btn {
  margin-top: 16px;
  height: 88px !important;
  border-radius: 44px !important;
  border: none !important;
  background: linear-gradient(270deg, #e6c3a1, #7e5678) !important;
  font-size: 32px !important;
  font-weight: 700 !important;
  color: #fff !important;
  box-shadow: 0 6px 20px rgba(126,86,120,0.3);
}

::v-deep .van-picker__toolbar {
  height: 96px;
}
::v-deep .van-picker__cancel,
::v-deep .van-picker__confirm {
  font-size: 32px;
  padding: 0 40px;
}
::v-deep .van-picker-column__item {
  font-size: 34px;
}
::v-deep .van-picker-column__item--selected {
  font-size: 36px;
  font-weight: 600;
  color: #7e5678;
}
</style>
